import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import ComicCard from '@/components/feature/ComicCard';
import { allComics } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

const chapterTitles = ['风暴降临', '旧友重逢', '暗流涌动', '抉择之时', '逆风而行', '尘埃落定'];

export default function ComicDetail() {
  const { id } = useParams();
  const comic = allComics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh]">
          <NotFound />
        </main>
        <Footer />
      </>
    );
  }

  const related = allComics
    .filter((c) => c.id !== comic.id && (c.type === comic.type || c.genres.some((g) => comic.genres.includes(g))))
    .slice(0, 4);

  const chapters = Array.from({ length: 6 }, (_, i) => ({
    num: comic.chapters - i,
    title: chapterTitles[i % chapterTitles.length],
  }));

  const structured = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://oiutc07.cn/' },
          { '@type': 'ListItem', position: 2, name: comic.type === 'guoman' ? '国漫' : '日漫', item: `https://oiutc07.cn/${comic.type}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `https://oiutc07.cn/comic/${comic.id}` },
        ],
      },
      {
        '@type': 'ComicStory',
        name: comic.title,
        description: comic.intro,
        image: comic.cover,
        author: { '@type': 'Organization', name: '虫虫漫画' },
        inLanguage: 'zh-CN',
        genre: comic.genres,
        url: `https://oiutc07.cn/comic/${comic.id}`,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }} />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <nav className="text-sm text-foreground-500" aria-label="面包屑">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to={comic.type === 'guoman' ? '/guoman' : '/riman'} className="hover:text-primary-600 transition-colors">{comic.type === 'guoman' ? '国漫' : '日漫'}</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground-700" aria-current="page">{comic.title}</li>
          </ol>
        </nav>

        <div className="mt-6 flex flex-col gap-8 md:flex-row">
          <div className="w-52 shrink-0 self-start md:w-64">
            <div className="relative overflow-hidden rounded-2xl shadow-lift">
              <img src={comic.cover} alt={`《${comic.title}》封面`} className="aspect-[3/4] w-full object-cover object-top" />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">{comic.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-sm font-medium text-background-50 ${comic.status === '连载中' ? 'bg-accent-500' : 'bg-secondary-500'}`}>
                {comic.status}
              </span>
              <span className="rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-600">{comic.type === 'guoman' ? '国漫' : '日漫'}</span>
              <span className="rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-600">{comic.year} 年</span>
              <span className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700">
                <i className="ri-star-fill text-accent-500"></i>{comic.rating.toFixed(1)}
              </span>
              <span className="rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-600">{comic.views} 人气</span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {comic.genres.map((g) => (
                <span key={g} className="rounded-full border border-primary-200 bg-background-100 px-3 py-1 text-sm text-primary-700">
                  {g}
                </span>
              ))}
            </div>

            <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">{comic.intro}</p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#chapters"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-book-open-line"></i>开始阅读
              </a>
              <span className="text-sm text-foreground-500">
                已收录 {comic.chapters} 话 · {comic.status === '连载中' ? '持续更新中' : '已全部完结'}
              </span>
            </div>
          </div>
        </div>

        <section id="chapters" className="mt-12">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
            目录<span className="text-primary-500">章节</span>
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((ch) => (
              <a
                key={ch.num}
                href={`#chapters`}
                className="flex items-center justify-between rounded-xl border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-background-50 hover:text-primary-700"
              >
                <span>第 {ch.num} 话 · {ch.title}</span>
                <i className="ri-play-circle-line text-lg text-foreground-400"></i>
              </a>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
              相关<span className="text-primary-500">推荐</span>
            </h2>
            <Link to={comic.type === 'guoman' ? '/guoman' : '/riman'} className="text-sm font-medium text-primary-600 hover:text-primary-700">
              更多{comic.type === 'guoman' ? '国漫' : '日漫'} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}